import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useSharedBenModel } from '../hooks/useSharedModel';
import './StartupHomepage.css';

// Hero 3D Model Component
const HeroModel: React.FC = () => {
  const { scene, geometryCenter } = useSharedBenModel('/models/cancer/ben.glb');
  const modelRef = useRef<any>(null);
  const [rotationStartTime, setRotationStartTime] = useState<number | null>(null);

  useEffect(() => {
    setRotationStartTime(Date.now());
  }, []);

  useFrame(() => {
    if (modelRef.current && rotationStartTime) {
      const elapsed = (Date.now() - rotationStartTime) / 1000;
      modelRef.current.rotation.y = elapsed * 0.3;
    }
  });

  if (!scene) return null;

  return (
    <group ref={modelRef} scale={[1.2, 1.2, 1.2]}>
      <group position={[-geometryCenter.x, -geometryCenter.y, -geometryCenter.z]}>
        <primitive object={scene} />
      </group>
    </group>
  );
};

const StartupHomepage: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className={`startup-homepage ${isLoaded ? 'loaded' : ''}`}>
      {/* Header/Navigation */}
      <header className="header">
        <div className="container">
          <div className="nav-brand">
            <img src="/logo.png" alt="Allegoria" className="logo" />
          </div>
          <nav className="nav-links">
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                3D Foundation AI for Dermatology
              </h1>
              <p className="hero-subtitle">
                Allegoria is AI for dermatology images that understands the 3D structure of skin,
                enabling unique state-of-the-art image analysis in all skin conditions and 2D images
              </p>
              <div className="hero-buttons">
                <a href="https://challenge.isic-archive.com/leaderboards/milk10k/"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="primary-button">
                  Public Leaderboard
                </a>
                <a href="https://docsend.com/view/i82b7fg34fbmdick"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="secondary-button">
                  View Pitch Deck
                </a>
              </div>
            </div>
            <div className="hero-3d">
              <div className="hero-canvas-container">
                <Canvas
                  camera={{ position: [0, 0, 25], fov: 50 }}
                  gl={{
                    powerPreference: "default",
                    antialias: true,
                    alpha: true,
                    preserveDrawingBuffer: false,
                  }}
                  style={{ background: 'transparent' }}
                  dpr={Math.min(window.devicePixelRatio, 1.5)}
                >
                  <ambientLight intensity={0.6} />
                  <directionalLight
                    position={[10, 10, 5]}
                    intensity={0.8}
                    castShadow={false}
                  />
                  <directionalLight
                    position={[-10, -10, -5]}
                    intensity={0.4}
                  />
                  <pointLight position={[0, 5, 0]} intensity={0.3} color="#ffffff" />

                  <HeroModel />

                  <OrbitControls
                    enablePan={false}
                    enableZoom={true}
                    enableRotate={true}
                    autoRotate={false}
                    makeDefault
                    enableDamping={true}
                    dampingFactor={0.05}
                  />
                </Canvas>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer bar */}
      <footer className="footer" style={{ padding: '1rem 0', background: '#000', borderTop: 'none' }} />
    </div>
  );
};

export default StartupHomepage;